import { Box, Card, CardContent, Stack, Typography, Button, Color } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  BookmarkAdd02Icon,
  Restaurant02Icon,
  Settings02Icon,
  CheckmarkCircle01Icon,
  CircleIcon,
} from "@hugeicons/core-free-icons";
import { MenuSection } from "@/types/menus";
import { blue, green, orange } from "@mui/material/colors";

interface FirstStepsProps {
  structure: MenuSection[];
  onAddSection?: () => void;
}

const FirstSteps = ({ structure = [], onAddSection }: FirstStepsProps) => {
  // Check Step 1: Agrega una categoría (sections exist)
  const isStep1Completed = structure.length > 0;

  // Check Step 2: Arrastra platos (at least one product in any section)
  const isStep2Completed = structure.some((s) => s.products && s.products.length > 0);

  // Check Step 3: Personaliza tus secciones (at least one section has columns > 1 or template non-default or visible false)
  const isStep3Completed = structure.some(
    (s) =>
      (s.columns && s.columns > 1) ||
      (s.template && s.template !== "default-filled") ||
      s.isVisible === false
  );

  // Determine active step
  let activeStep = 1;

  if (isStep1Completed) {
    activeStep = 2;
  }
  if (isStep1Completed && isStep2Completed) {
    activeStep = 3;
  }
  if (isStep1Completed && isStep2Completed && isStep3Completed) {
    activeStep = 4; // All completed
  }

  const renderCard = (
    title: string,
    description: string,
    icon: any,
    isCompleted: boolean,
    isActive: boolean,
    iconBgColor: string,
    actionButton?: React.ReactNode,
  ) => {
    // Styles depending on state
    let bgColor = "#ffffff";
    let borderColor = "#e0e0e0";
    let iconColor = "#ffffff";
    let stepTitleColor = "#0052cc";

    if (!isCompleted && !isActive) {
      // Locked/inactive but still not completed
      bgColor = "#ffffff";
      borderColor = "#e0e0e0";
      iconColor = "#ffffff";
      stepTitleColor = "#90caf9";
    }

    return (
      <Card
        variant="outlined"
        sx={{
          bgcolor: bgColor,
          borderColor: isCompleted ? borderColor : isActive ? "#0052cc" : borderColor,
          borderRadius: 3,
          transition: "all 0.2s ease-in-out",
          boxShadow: isActive && !isCompleted ? 2 : 0,
        }}
      >
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
          <Stack spacing={1.5}>
            {/* Header row: Icon, STEP number, checkmark */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1,
                    borderRadius: 2,
                    bgcolor: iconBgColor,
                    color: iconColor,
                  }}
                >
                  <HugeiconsIcon icon={icon} size={20} strokeWidth={2.5} />
                </Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color={stepTitleColor}
                  sx={{ textTransform: "uppercase", letterSpacing: 1 }}
                >
                  {title}
                </Typography>
              </Stack>

              <HugeiconsIcon
                icon={isCompleted ? CheckmarkCircle01Icon : CircleIcon}
                size={20}
                color={isCompleted ? "#2e7d32" : isActive ? "#0052cc" : "#bdbdbd"}
                strokeWidth={isCompleted ? 2.5 : 2}
              />
            </Stack>

            {/* Title & Description */}
            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
              {description}
            </Typography>

            {/* Action button if applicable */}
            {!isCompleted && isActive && actionButton}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const addCategoryButton = onAddSection && (
    <Button
      variant="contained"
      color="primary"
      size="small"
      onClick={onAddSection}
    >
      Crear Sección
    </Button>
  );

  return (
    <Stack spacing={2} sx={{ p: 2.5 }}>
      {renderCard(
        "Estructura",
        "Agrega una categoría/sección para organizar tu contenido y permitirle a tus clientes navegar por el mas facilmente. Agrega cuantas necesites.",
        BookmarkAdd02Icon,
        isStep1Completed,
        activeStep === 1,
        blue[500],
        addCategoryButton,
      )}

      {renderCard(
        "Productos",
        "Agrega tantos productos como necesites a tus secciones. Puedes ordenarlos dentro de cada sección arrastrandolos verticalmente entre si.",
        Restaurant02Icon,
        isStep2Completed,
        activeStep === 2,
        orange[500],
      )}

      {renderCard(
        "Personalización",
        "Presiona el icono de engranaje en las secciones y configura estilo de platos, columnas y mas.",
        Settings02Icon,
        isStep3Completed,
        activeStep === 3,
        green[500],
      )}
    </Stack>
  );
};

export default FirstSteps;
